import {
	BaselineCmdsInfo,
	useBaselineCmds,
} from 'features/Baselines/utils/baselineCmds';
import {ProjectDetailsPageParams} from 'features/VehicleProjects/vehicleProjectPagePaths';
import {
	TooltipTranslationOptions,
	createPathWithTooltipsTextKey,
	createTooltipTranslationProviderFromNamespace,
} from 'features/localizedTooltips';
import React, {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {LoadWrapper} from 'components/LoadWrapper';
import {BaselinesList} from './BaselinesList';
import {useGetBaselinesByModelAndSeriesQuery} from '../hooks/baseline.generated';
import {
	BreadcrumbCompat as Breadcrumb,
	IBreadcrumbItem,
} from 'components/FluentUICompat';
import {useGenerationBreadcrumbs} from '../utils/breadcrumbs.utils';
import {
	translationNamespace,
	useBaselineListPageTranslations,
} from './BaselinesPage.utils';
import {useBaselineListCmds} from './baselineListCmds';
import {BaselineCreateDialog} from '../components/BaselineCreateDialog';
import {BaselineRenameDialog} from '../components/BaselineRenameDialog';
import {Baseline} from 'types';
import {BaselinesSelectionDialog} from '../BaselineComparisonPage/BaselinesSelectionDialog/BaselinesSelectionDialog';
import {PossibleSelectedBaseline} from '../BaselineComparisonPage/BaselinesSelectionDialog/BaselinesSelectionDialog.types';

const TranslationProvider =
	createTooltipTranslationProviderFromNamespace(translationNamespace);

const emptyArray: never[] = [];

export const useBreadcrumbsOfBaselineListPage = (
	params: ProjectDetailsPageParams,
	pageBreadcrumbFields?: Partial<IBreadcrumbItem>,
): IBreadcrumbItem[] => {
	const {t} = useBaselineListPageTranslations();

	const extraItems: IBreadcrumbItem[] = useMemo(() => {
		const createPageBreadcrumb = (): IBreadcrumbItem => {
			return {
				key: 'baselines',
				text: t('BreadcrumbText'),
				...pageBreadcrumbFields,
			};
		};

		return [createPageBreadcrumb()];
	}, [t, pageBreadcrumbFields]);

	return useGenerationBreadcrumbs({params, extraItems});
};

export function BaselineListPage(): React.ReactElement {
	const params = useParams() as ProjectDetailsPageParams;
	const breadcrumbs: IBreadcrumbItem[] =
		useBreadcrumbsOfBaselineListPage(params);
	const {data, loading, startPolling, stopPolling} =
		useGetBaselinesByModelAndSeriesQuery({
			variables: params,
			fetchPolicy: 'no-cache',
		});

	const refetchCountMax = 20;
	const [refetchCount, setRefetchCount] = React.useState(0);
	React.useEffect(() => {
		if (!loading) {
			const baselines = data?.vehicleProjectBaselinesByModelAndSeries ?? [];
			const baselineExportReady = baselines.filter(
				x => (x.reqIFAttachments ?? []).length > 0,
			);
			const allBaselineExportReady =
				baselines.length === baselineExportReady.length;
			if (allBaselineExportReady) {
				setRefetchCount(0);
			}
			if (!allBaselineExportReady && refetchCount < refetchCountMax) {
				setRefetchCount(x => x + 1);
				startPolling(2000);
			}
		}
	}, [data, loading, refetchCountMax]);

	const [baselines, setBaselines] = useState<Baseline[]>([]);
	const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);

	useBaselineListCmds({params}, baselines, setRenameDialogOpen);

	const baselineList =
		data?.vehicleProjectBaselinesByModelAndSeries ?? emptyArray;

	const {infoForIsSuccessDialogOpen}: BaselineCmdsInfo = useBaselineCmds({
		params,
		showOverviewCmd: false,
	});

	const translationHookOptions: TooltipTranslationOptions = useMemo(() => {
		return {keyPrefix: createPathWithTooltipsTextKey('Columns')};
	}, []);

	return (
		<>
			<Breadcrumb items={breadcrumbs} />
			<LoadWrapper loading={loading}>
				<TranslationProvider translationHookOptions={translationHookOptions}>
					<BaselinesList
						baselineList={baselineList}
						params={params}
						setBaselines={setBaselines}
					/>
				</TranslationProvider>
			</LoadWrapper>
			<BaselineCreateDialog
				generation={params.generation}
				modelSeries={params.modelSeries}
				infoForIsOpen={infoForIsSuccessDialogOpen}
			/>
			<BaselineRenameDialog
				baselines={baselines}
				renameDialogOpen={renameDialogOpen}
				setRenameDialogOpen={setRenameDialogOpen}
			/>
			<BaselinesSelectionDialog
				params={params}
				baseline1Initial={baselines.at(0) ?? null}
			/>
		</>
	);
}
